/* Admin: one column setting for all packer accounts (what packers see on the Packer screen). */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Camera, Loader2 } from "lucide-react";
import { API, getAuthHeader } from "@/App";
import { Button } from "@/components/ui/button";

export const PackerColumnsCard = () => {
  const [all, setAll] = useState([]);
  const [cols, setCols] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    axios.get(`${API}/order-register/packer/settings`, { headers: getAuthHeader() })
      .then(r => { setAll(r.data.all_columns); setCols(r.data.columns); }).catch(() => {});
  }, []);
  const flip = (k) => setCols(c => (c.includes(k) ? c.filter(x => x !== k) : [...c, k]));
  const save = async () => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/order-register/packer/settings`, { columns: cols }, { headers: getAuthHeader() });
      setCols(r.data.columns); toast.success("Packing Team columns saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    setBusy(false);
  };
  if (!all.length) return null;
  return (
    <div className="rounded-lg border border-teal-200 bg-teal-50/40 px-4 py-2" data-testid="packer-columns-card">
      <button type="button" onClick={() => setOpen(o => !o)} className="flex w-full items-center gap-2 text-left text-sm font-semibold text-teal-900" data-testid="packer-columns-toggle">
        <Camera className="h-4 w-4" /> Packing Team screen columns
        <span className="text-xs font-normal text-teal-700">({cols.length} shown · one setting for the whole Packing Team)</span>
        <span className="ml-auto text-xs text-indigo-600">{open ? "Hide" : "Edit"}</span>
      </button>
      {open && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {all.map(c => (
            <label key={c.key} className={`inline-flex items-center gap-1 rounded border bg-white px-2 py-1 text-xs ${c.locked ? "opacity-70" : "cursor-pointer"}`} data-testid={`packer-col-${c.key}`}>
              <input type="checkbox" checked={c.locked || cols.includes(c.key)} disabled={c.locked} onChange={() => flip(c.key)} /> {c.label}
            </label>))}
          <Button size="sm" className="h-7 bg-teal-700 text-xs hover:bg-teal-800" disabled={busy} onClick={save} data-testid="packer-columns-save">
            {busy && <Loader2 className="mr-1 h-3 w-3 animate-spin" />} Save</Button>
        </div>)}
    </div>
  );
};

// Wrong photos (AI flagged) + packer scorecard for a day
export const WrongPhotosCard = () => {
  const [day, setDay] = useState(() => new Date(Date.now() + 5.5 * 3600e3).toISOString().slice(0, 10));
  const [d, setD] = useState(null);
  useEffect(() => {
    axios.get(`${API}/order-register/packer/checks/report?day=${day}`, { headers: getAuthHeader() }).then(r => setD(r.data)).catch(() => setD(null));
  }, [day]);
  return (
    <div className="rounded-lg border border-rose-200 bg-white px-4 py-3 text-xs" data-testid="wrong-photos-card">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-rose-900">Wrong photos &amp; packer scorecard</span>
        <input type="date" value={day} onChange={e => setDay(e.target.value)} className="h-7 rounded border px-2" data-testid="wrong-photos-day" />
        {d && <span className="text-slate-500">{d.total} photo check(s) · {d.flagged.length} flagged</span>}
      </div>
      {d?.scorecard?.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2" data-testid="packer-scorecard">
          {d.scorecard.map(s => (
            <span key={s.name} className="rounded-lg border bg-slate-50 px-2 py-1" data-testid={`score-${s.name}`}>
              <b>{s.name}</b> · {s.photos} photo(s) · {s.matched_pct === null ? "—" : <b className={s.matched_pct >= 90 ? "text-emerald-700" : s.matched_pct >= 70 ? "text-amber-700" : "text-rose-700"}>{s.matched_pct}% matched</b>}
              {s.wrong > 0 && <span className="text-rose-700"> · {s.wrong} wrong</span>}</span>))}
        </div>)}
      {d?.flagged?.length ? (
        <table className="w-full text-left"><thead className="text-slate-500"><tr><th className="py-1">Time</th><th>Customer</th><th>Packed by</th><th>AI saw</th><th>Retaken</th></tr></thead>
          <tbody>{d.flagged.map(c => (
            <tr key={c.id} className="border-t" data-testid={`wrong-photo-${c.id}`}>
              <td className="py-1">{new Date(c.at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</td>
              <td><b>{c.customer_name}</b> <span className="text-slate-400">{c.tracking}</span></td>
              <td>{c.by || "—"}</td>
              <td className="text-rose-700">{c.note}{c.wrong?.length ? ` · on photo: ${c.wrong.join(", ")}` : ""}</td>
              <td>{c.retaken ? <span className="font-semibold text-emerald-700">✓ yes</span> : <span className="font-semibold text-rose-700">✗ not yet</span>}</td>
            </tr>))}</tbody></table>
      ) : d && <p className="text-emerald-700">No wrong photos flagged on this day.</p>}
    </div>
  );
};

export const PackedByNamesCard = () => {
  const [names, setNames] = useState(null);
  const [nm, setNm] = useState("");
  useEffect(() => {
    axios.get(`${API}/order-register/packer/settings`, { headers: getAuthHeader() }).then(r => setNames(r.data.packed_by_names || [])).catch(() => {});
  }, []);
  const save = async (list) => {
    try { const r = await axios.put(`${API}/order-register/packer/packed-by-names`, { names: list }, { headers: getAuthHeader() }); setNames(r.data.packed_by_names); toast.success("Packed by names saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };
  if (names === null) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-teal-200 bg-white px-4 py-2 text-xs" data-testid="packed-by-card">
      <span className="text-sm font-semibold text-teal-900">Packed by names:</span>
      {names.map(n => (
        <span key={n} className="inline-flex items-center gap-1 rounded-full bg-teal-50 px-2 py-0.5 font-semibold text-teal-800" data-testid={`packed-by-${n}`}>{n}
          <button type="button" onClick={() => save(names.filter(x => x !== n))} className="text-rose-500" title="Remove">×</button></span>))}
      <input value={nm} onChange={e => setNm(e.target.value)} onKeyDown={e => e.key === "Enter" && nm.trim() && (save([...names, nm.trim()]), setNm(""))}
        placeholder="Add a name…" className="h-7 w-36 rounded border px-2" data-testid="packed-by-input" />
      <Button size="sm" className="h-7 bg-teal-700 text-xs hover:bg-teal-800" disabled={!nm.trim()} onClick={() => { save([...names, nm.trim()]); setNm(""); }} data-testid="packed-by-add">Add</Button>
      <span className="text-slate-500">Packers pick their name before sending photos{names.length ? "" : " (no names → no dropdown)"}</span>
    </div>
  );
};

export const PhotoHandlersCard = () => {
  const [d, setD] = useState(null);
  const [ids, setIds] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    axios.get(`${API}/order-register/packer/photo-handlers`, { headers: getAuthHeader() })
      .then(r => { setD(r.data); setIds(r.data.handler_ids); }).catch(() => {});
  }, []);
  const save = async () => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/order-register/packer/photo-handlers`, { handler_ids: ids }, { headers: getAuthHeader() });
      setIds(r.data.handler_ids); toast.success("Product photo request handler saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    setBusy(false);
  };
  if (!d) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-teal-200 bg-white px-4 py-2 text-xs" data-testid="photo-handlers-card">
      <span className="text-sm font-semibold text-teal-900">Product photo requests go to:</span>
      {d.packing_team.map(u => (
        <label key={u.id} className="inline-flex cursor-pointer items-center gap-1 rounded border px-2 py-1" data-testid={`photo-handler-${u.id}`}>
          <input type="checkbox" checked={ids.includes(u.id)} onChange={() => setIds(s => (s.includes(u.id) ? s.filter(x => x !== u.id) : [...s, u.id]))} /> {u.name || u.email}
        </label>))}
      {!d.packing_team.length && <span className="text-slate-400">No Packing Team users yet</span>}
      <span className="text-slate-500">{ids.length ? "" : "(none ticked → whole Packing Team)"} · office team always sees them</span>
      <Button size="sm" className="ml-auto h-7 bg-teal-700 text-xs hover:bg-teal-800" disabled={busy} onClick={save} data-testid="photo-handlers-save">
        {busy && <Loader2 className="mr-1 h-3 w-3 animate-spin" />} Save</Button>
    </div>
  );
};
